import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import appConfig from '../../consumingApi/services/appConfig';
import LogisticService from '@LogisticService';

const BASE_URL = appConfig.logisticsUrl;
const REDUCER_NAME = 'attachmentSlice';
const GET_ATTACHMENT_OS_TABLE_DATA = 'attachmentSlice/getAttachmentOsTableData';
const GET_ATTACHMENT_OS_DETAILS = 'attachmentSlice/getAttachmentOsDetails';
const GET_ATTACHMENT_STONE_ENTREGA = 'attachmentSlice/getAttachmentStoneEntrega';
const GET_ATTACHMENT_STONE_ENTREGA_DETAILS = 'attachmentSlice/getAttachmentStoneEntregaDetails';

const initialState = {
    getAttachmentOsTableData: [],
    getAttachmentOsTableDataStatus: 'idle',
    getAttachmentOsTableDataStatusError: 'idle',

    getAttachmentOsDetailsData: [],
    getAttachmentOsDetailsStatus: 'idle',
    getAttachmentOsDetailsError: null,

    getAttachmentStoneEntregaTableData: [],
    getAttachmentStoneEntregaTableDataStatus: 'idle',
    getAttachmentStoneEntregaTableDataStatusError: 'idle',

    getAttachmentStoneEntregaDetailsData: [],
    getAttachmentStoneEntregaDetailsStatus: 'idle',
    getAttachmentStoneEntregaDetailsError: null,
};

export const getAttachmentOs = createAsyncThunk(
    GET_ATTACHMENT_OS_TABLE_DATA,
    async ({ dataOs, poloName, orderNumber }, { rejectWithValue }) => {
        try {
            const params = new URLSearchParams();
            if (orderNumber) {
                params.append('order_number', orderNumber);
            } else {
                if (dataOs) params.append('date', dataOs);
                if (poloName) params.append('polo', poloName);
            }
            const url = `${BASE_URL}/ferramentas/anexos/dados_os?${params.toString()}`;

            const res = await LogisticService.requestWithAuth(url);

            return res.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message || "Erro desconhecido");
        }
    }
);

export const getAttachmentOsDetails = createAsyncThunk(
    GET_ATTACHMENT_OS_DETAILS,
    async (order_number, { rejectWithValue }) => {
        try {
            let url = `${BASE_URL}/ferramentas/anexos/detalhamento/dados_os/${order_number}`;

            const res = await LogisticService.requestWithAuth(url);

            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message || "Erro desconhecido");
        }
    }
);

export const getAttachmentStoneEntrega = createAsyncThunk(
    GET_ATTACHMENT_STONE_ENTREGA,
    async ({ dataOs, poloName, trackingCode }, { rejectWithValue }) => {
        try {
            const params = new URLSearchParams();
            if (trackingCode) {
                params.append('tracking_code', trackingCode);
            } else {
                if (dataOs) params.append('date', dataOs);
                if (poloName) params.append('polo', poloName);
            }
            const url = `${BASE_URL}/ferramentas/anexos/dados_stone_entrega?${params.toString()}`;

            const res = await LogisticService.requestWithAuth(url);

            return res.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message || "Erro desconhecido");
        }
    }
);

export const getAttachmentStoneEntregaDetails = createAsyncThunk(
    GET_ATTACHMENT_STONE_ENTREGA_DETAILS,
    async (trackingCode, { rejectWithValue }) => {
        try {
            let url = `${BASE_URL}/ferramentas/anexos/detalhamento/dados_stone_entrega/${trackingCode}`;

            const res = await LogisticService.requestWithAuth(url);

            return res.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message || "Erro desconhecido");
        }
    }
);

export const attachmentSlice = createSlice({
    name: REDUCER_NAME,
    initialState,
    reducers: {
        resetAttachmentOsImageStatus: (state) => {
            state.getAttachmentOsImageStatus = 'idle';
        },
        resetAttachmentStoneEntregaDetails: (state) => {
            state.getAttachmentStoneEntregaDetailsData = [];
            state.getAttachmentStoneEntregaDetailsStatus = 'idle';
            state.getAttachmentStoneEntregaDetailsError = null;
        },
        // Novo reducer para limpar os dados da OS ao fechar o Drawer
        resetAttachmentOsDetails: (state) => {
            state.getAttachmentOsDetailsData = null;
            state.getAttachmentOsDetailsStatus = 'idle';
            state.getAttachmentOsDetailsError = null;
        },
        // Limpa os dados da tabela de OS ao entrar na tela
        resetAttachmentOsTableData: (state) => {
            state.getAttachmentOsTableData = [];
            state.getAttachmentOsTableDataStatus = 'idle';
            state.getAttachmentOsTableDataStatusError = 'idle';
        },
        // Limpa os dados da tabela de Stone Entrega ao entrar na tela
        resetAttachmentStoneEntregaTableData: (state) => {
            state.getAttachmentStoneEntregaTableData = [];
            state.getAttachmentStoneEntregaTableDataStatus = 'idle';
            state.getAttachmentStoneEntregaTableDataStatusError = 'idle';
        }
    },
    extraReducers: (builder) => {
        builder
            // getAttachmentOs
            .addCase(getAttachmentOs.pending, (state) => {
                state.getAttachmentOsTableDataStatus = 'pending';
                state.getAttachmentOsTableDataStatusError = null;
            })
            .addCase(getAttachmentOs.fulfilled, (state, action) => {
                state.getAttachmentOsTableData = action.payload;
                state.getAttachmentOsTableDataStatus = 'fulfilled';
            })
            .addCase(getAttachmentOs.rejected, (state, action) => {
                state.getAttachmentOsTableDataStatusError = 'rejected';
                state.getAttachmentOsTableDataStatus = 'rejected';
            })

            // getAttachmentOsDetails
            .addCase(getAttachmentOsDetails.pending, (state) => {
                state.getAttachmentOsDetailsStatus = 'pending';
                state.getAttachmentOsDetailsError = null;
            })
            .addCase(getAttachmentOsDetails.fulfilled, (state, action) => {
                state.getAttachmentOsDetailsData = action.payload;
                state.getAttachmentOsDetailsStatus = 'fulfilled';
            })
            .addCase(getAttachmentOsDetails.rejected, (state, action) => {
                state.getAttachmentOsDetailsError = action.payload || 'rejected';
                state.getAttachmentOsDetailsStatus = 'rejected';
            })

            // getAttachmentStoneEntrega
            .addCase(getAttachmentStoneEntrega.pending, (state) => {
                state.getAttachmentStoneEntregaTableDataStatus = 'pending';
                state.getAttachmentStoneEntregaTableDataStatusError = null;
            })
            .addCase(getAttachmentStoneEntrega.fulfilled, (state, action) => {
                state.getAttachmentStoneEntregaTableData = action.payload;
                state.getAttachmentStoneEntregaTableDataStatus = 'fulfilled';
            })
            .addCase(getAttachmentStoneEntrega.rejected, (state, action) => {
                state.getAttachmentStoneEntregaTableDataStatusError = 'rejected';
                state.getAttachmentStoneEntregaTableDataStatus = 'rejected';
            })

            // getAttachmentStoneEntregaDetails
            .addCase(getAttachmentStoneEntregaDetails.pending, (state) => {
                state.getAttachmentStoneEntregaDetailsStatus = 'pending';
                state.getAttachmentStoneEntregaDetailsError = null;
            })
            .addCase(getAttachmentStoneEntregaDetails.fulfilled, (state, action) => {
                state.getAttachmentStoneEntregaDetailsData = action.payload;
                state.getAttachmentStoneEntregaDetailsStatus = 'fulfilled';
            })
            .addCase(getAttachmentStoneEntregaDetails.rejected, (state, action) => {
                state.getAttachmentStoneEntregaDetailsError = action.payload || 'rejected';
                state.getAttachmentStoneEntregaDetailsStatus = 'rejected';
            });
    },
});

export const {
    resetAttachmentOsImageStatus,
    resetAttachmentStoneEntregaDetails,
    resetAttachmentOsDetails,
    resetAttachmentOsTableData,
    resetAttachmentStoneEntregaTableData
} = attachmentSlice.actions;

export const attachmentSelector = (state) => state.attachmentSlice;

export default attachmentSlice.reducer;
